import { useCallback, useMemo } from 'react';
import {
  DEFAULT_PAGINATION_PAGE,
  PAGINATION_PAGE_PARAM,
  PAGINATION_PAGE_SIZE_PARAM,
} from '@shared/constants/pagination.constants';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { formatCalendarDate, parseCalendarDate } from '@shared/utils/date';
import {
  DEFAULT_REGISTRATION_PAGE_SIZE,
  MAX_REGISTRATION_PAGE_SIZE,
  MAX_REGISTRATION_SEARCH_LENGTH,
  REGISTRATION_STATUS_OPTIONS,
  REGISTRATION_VALIDITY_OPTIONS,
} from '../constants';
import type { TRegistrationFilters } from '../types';

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

// Filters live in the URL under their API names. Hand-edited values the API would reject are dropped.
const readList = (searchParams: URLSearchParams, key: keyof TRegistrationFilters): string[] => [
  ...new Set(searchParams.getAll(key).flatMap((value) => value.split(','))),
];

const readDate = (searchParams: URLSearchParams, key: keyof TRegistrationFilters): string => {
  const value = searchParams.get(key) ?? '';
  return formatCalendarDate(parseCalendarDate(value));
};

const readPositiveInteger = (value: string | null, fallback: number): number => {
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : fallback;
};

export const parseRegistrationFilters = (searchParams: URLSearchParams): TRegistrationFilters => {
  const status = readList(searchParams, 'status');
  const validity = searchParams.get('validity');
  return {
    search: (searchParams.get('search') ?? '').slice(0, MAX_REGISTRATION_SEARCH_LENGTH),
    countryIds: readList(searchParams, 'countryIds').filter((id) => OBJECT_ID_PATTERN.test(id)),
    productIds: readList(searchParams, 'productIds').filter((id) => OBJECT_ID_PATTERN.test(id)),
    status: REGISTRATION_STATUS_OPTIONS.map(({ value }) => value).filter((value) => status.includes(value)),
    validity: REGISTRATION_VALIDITY_OPTIONS.find(({ value }) => value === validity)?.value,
    validUntilFrom: readDate(searchParams, 'validUntilFrom'),
    validUntilTo: readDate(searchParams, 'validUntilTo'),
  };
};

export const useRegistrationsUrlState = () => {
  const { searchParams, changeMany } = useSearchParams();
  const filters = useMemo(() => parseRegistrationFilters(searchParams), [searchParams]);
  const page = readPositiveInteger(searchParams.get(PAGINATION_PAGE_PARAM), DEFAULT_PAGINATION_PAGE);
  const pageSize = Math.min(
    readPositiveInteger(searchParams.get(PAGINATION_PAGE_SIZE_PARAM), DEFAULT_REGISTRATION_PAGE_SIZE),
    MAX_REGISTRATION_PAGE_SIZE
  );

  // Every filter change starts again at the first page.
  const setFilters = useCallback(
    (changes: Partial<TRegistrationFilters>) =>
      changeMany({
        ...Object.fromEntries(
          Object.entries(changes).map(([key, value]) => [key, Array.isArray(value) ? value : value || undefined])
        ),
        [PAGINATION_PAGE_PARAM]: undefined,
      }),
    [changeMany]
  );

  const setPage = useCallback(
    (nextPage: number, { replace = false }: { replace?: boolean } = {}) =>
      changeMany({ [PAGINATION_PAGE_PARAM]: nextPage > 1 ? String(nextPage) : undefined }, { replace }),
    [changeMany]
  );

  const setPageSize = useCallback(
    (nextPageSize: number) =>
      changeMany({
        [PAGINATION_PAGE_SIZE_PARAM]: String(Math.min(Math.max(nextPageSize, 1), MAX_REGISTRATION_PAGE_SIZE)),
        [PAGINATION_PAGE_PARAM]: undefined,
      }),
    [changeMany]
  );

  return { filters, page, pageSize, setFilters, setPage, setPageSize };
};
